import { Directive, EventEmitter, Input, Output } from '@angular/core';
import { SortCriterion, SortDirection } from './multi-sort.util';

/**
 * Multi-orden reutilizable (estilo MatSort, pero para varias columnas a la vez).
 * Se aplica sobre el `mat-table` con `exportAs="mhMultiSort"`. Mantiene la lista de
 * criterios (columna + dirección, por prioridad), expone helpers para el template y
 * emite los criterios al cambiar. El orden de los datos lo aplica el componente
 * (con `multiSortComparator`), porque el accessor de valores es propio de cada tabla.
 *
 * Uso en el template:
 *   <mat-table mhMultiSort #ms="mhMultiSort" [mhMultiSortInitial]="defaultSort"
 *              (mhMultiSortChange)="onSortChange($event)">
 *     ...
 *     <mat-header-cell (click)="ms.toggle(key, $event.shiftKey)"> ... </mat-header-cell>
 */
@Directive({
  selector: '[mhMultiSort]',
  exportAs: 'mhMultiSort',
  standalone: false,
})
export class MultiSortDirective {
  @Input('mhMultiSortInitial') set initial(criteria: SortCriterion[]) {
    if (criteria) this.criteria = [...criteria];
  }

  @Output('mhMultiSortChange') sortChange = new EventEmitter<SortCriterion[]>();

  criteria: SortCriterion[] = [];

  /** Alterna el orden de una columna. Con shift se acumula como criterio adicional. */
  toggle(column: string, shiftKey: boolean): void {
    const idx = this.criteria.findIndex((c) => c.active === column);

    if (shiftKey) {
      if (idx > -1) {
        const next = this.next(this.criteria[idx].direction);
        if (next) this.criteria[idx] = { active: column, direction: next };
        else this.criteria.splice(idx, 1);
      } else {
        this.criteria.push({ active: column, direction: 'asc' });
      }
    } else {
      if (this.criteria.length === 1 && idx === 0) {
        const next = this.next(this.criteria[0].direction);
        this.criteria = next ? [{ active: column, direction: next }] : [];
      } else {
        this.criteria = [{ active: column, direction: 'asc' }];
      }
    }

    this.sortChange.emit(this.criteria);
  }

  getDirection(column: string): SortDirection | null {
    return this.criteria.find((c) => c.active === column)?.direction ?? null;
  }

  getPriority(column: string): number | null {
    const idx = this.criteria.findIndex((c) => c.active === column);
    return idx > -1 ? idx + 1 : null;
  }

  get count(): number {
    return this.criteria.length;
  }

  // Ciclo de dirección: asc → desc → (sale del orden)
  private next(direction: SortDirection): SortDirection | null {
    return direction === 'asc' ? 'desc' : null;
  }
}
